import { useState } from "react";
import { CheckIcon } from "@/lib/icons";

/**
 * A compact, copyable identifier (SID, connection id, …). Renders the value in
 * a monospace chip with a copy button that flips to a check on success. Used
 * where users need to paste an id into their own scripts (e.g. the dialer API).
 */
export function CopyableId({
  value,
  label,
  className,
}: {
  value: string;
  /** Accessible name, e.g. "connection ID". Falls back to the value. */
  label?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — no-op */
    }
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void copy();
      }}
      aria-label={`Copy ${label ?? value}`}
      title="Copy"
      className={
        "group inline-flex max-w-full items-center gap-1 rounded px-1 py-0.5 font-mono text-[11px] " +
        "text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-700 focus-visible:outline-none " +
        "focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-zinc-500 dark:hover:bg-zinc-800/60 " +
        "dark:hover:text-zinc-300 " +
        (className ?? "")
      }
    >
      <span className="truncate">{value}</span>
      {copied ? (
        <CheckIcon size={12} className="flex-none text-emerald-500 dark:text-emerald-400" />
      ) : (
        <CopyGlyph />
      )}
      {copied && <span className="sr-only">Copied</span>}
    </button>
  );
}

function CopyGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="none"
      aria-hidden="true"
      className="flex-none opacity-60 transition group-hover:opacity-100"
    >
      <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M5 15V5a2 2 0 0 1 2-2h8"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}
